import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOMServer from "react-dom/server";
import { useDispatch } from "react-redux";
import { Swiper, SwiperSlide } from "swiper/react";
import { setClusterFeature } from "../../../../redux/action/ClusterFeature-action";
import { view } from "../../../../utils/API";
import { renderCell } from "../../../../utils/helper";

import { LineSymbol } from "../../../../data/constants";
import { LeftArrowIcon } from "../../../Icons";
import {
  StyledCustomPopupWrapperContainer,
  StyledPopUpTitle,
  StyledPopupTitleWrapper,
} from "../../CustomPopupWrapper-styled";
import getAllowedFields from "../../helpers/getAllowedFields";
import getClusterAttributeFields from "../../helpers/getClusterAttributeFields";
import { StyledNavButton } from "../../style/ClusterDetails/ClusterDetails-styled";
import ClusterList from "./ClusterList";

const Cluster = ({
  graphic,
  t,
  config,
  layer,
  initial = 0,
  clusterFeatures = [],
  isMobileApp = false,
  onRendered,
}) => {
  useEffect(() => {
    if (onRendered) {
      onRendered();
    }
  }, [onRendered]);

  const [features, setFeatures] = useState([]);
  const [clusterField, setClusterField] = useState();
  const [clusterFieldTitle, setClusterFieldTitle] = useState();
  const [featureIcons, setFeatureIcons] = useState({});
  const cluster = layer.layerConfig.cluster;
  const ref = useRef();
  const dispatch = useDispatch();

  const handlePopupSetup = (features) => {
    let fieldFeature;

    if (features.length > 0 && graphic.isAggregate) {
      fieldFeature =
        features.find((feat) => feat.attributes[cluster.clusterAttribute1]) ??
        features[0];
    } else {
      fieldFeature = graphic;
    }

    setFeatures(features);

    const fields = getAllowedFields(layer, fieldFeature);

    if (fields.length > 0) {
      let clusterFields = getClusterAttributeFields(cluster, fields, t);
      setClusterField(clusterFields.field1);

      setClusterFieldTitle(clusterFields.fieldTitle1);

      if (!view.popup.title) {
        view.popup.title = clusterFields.fieldTitle1 || layer.getLayerTitle(t);
      }

      loadMissingIcons(features, clusterFields.field1);
    }
  };

  const loadMissingIcons = async (features, field) => {
    try {
      const featuresWithoutValues = features.filter(
        (feat) =>
          !feat.attributes[field?.name] || featureIcons[feat.getObjectId()]
      );

      const iconPromises = featuresWithoutValues.map(async (feat) => {
        const icon = await getFeatureIcon(feat);
        return { id: feat.getObjectId(), icon };
      });

      const loadedIcons = await Promise.all(iconPromises);
      setFeatureIcons((prevIcons) => {
        const newIcons = { ...prevIcons };
        loadedIcons.forEach((item) => {
          newIcons[item.id] = item.icon;
        });
        return newIcons;
      });
    } catch (error) {
      console.log(error);
    }
  };

  const getFeatureIcon = useCallback(
    async (feature) => {
      if (!layer?.renderer) return null;

      try {
        const symbol = await layer.renderer.getSymbolAsync(feature);

        let url;
        if (symbol.url) {
          url = symbol.url;
        } else if (symbol.type === "simple-line" && symbol.color) {
          const color = symbol.color ? symbol.color.toHex() : "grey";
          url =
            "data:image/svg+xml;base64," +
            btoa(
              ReactDOMServer.renderToStaticMarkup(
                <LineSymbol stroke={color} dash={symbol.style === "dash"} />
              )
            );
        }

        return url ? <img src={url} alt="feature icon" /> : null;
      } catch (error) {
        console.error("Error getting feature icon:", error);
        return null;
      }
    },
    [layer]
  );

  useEffect(() => {
    // When returning from ClusterDetails popup we receive cluster features,
    // this is to fix issue after zooming in layerView.queryFeatures returns empty value
    if (clusterFeatures.length > 0) {
      handlePopupSetup(clusterFeatures);
      return;
    }

    view.whenLayerView(layer).then(async (layerView) => {
      try {
        const query = layerView.createQuery();
        query.aggregateIds = [graphic.getObjectId()];

        layerView.queryFeatures(query).then((result) => {
          handlePopupSetup(
            result.features.length > 0 && graphic.isAggregate
              ? result.features
              : [graphic]
          );
        });
      } catch (err) {
        console.log(err);
      }
    });
  }, [layer, graphic]);

  const filteredFeatures = useMemo(() => {
    if (!cluster.clusterAttribute2) {
      return features;
    }

    if (!graphic.isAggregate) return [graphic];

    const set = new Set();
    return features.filter((feat) => {
      const hasValue = set.has(feat.attributes[cluster.clusterAttribute1]);

      set.add(feat.attributes[cluster.clusterAttribute1]);
      return !hasValue;
    });
  }, [features, graphic]);

  const saveClusterFeature = useCallback(
    (feat, tabIndex = 0) => {
      const title = cluster.clusterAttribute2
        ? renderCell(
            clusterField,
            feat.attributes[clusterField?.name],
            t,
            config
          )
        : clusterFieldTitle;

      dispatch(
        setClusterFeature({
          graphic,
          title,
          tabIndex,
          features,
        })
      );
    },
    [graphic, clusterField, t, config, clusterFieldTitle, features]
  );

  const handleNavButtonClick = useCallback(
    (feat, tabIndex) => {
      if (cluster.clusterAttribute2) {
        const value = renderCell(
          clusterField,
          feat.attributes[clusterField?.name],
          t,
          config
        );

        view.popup.title = value || layer.getLayerTitle(t);

        ref.current?.slideTo(tabIndex);
      } else {
        saveClusterFeature(feat);
        view.openPopup({
          location: graphic.geometry,
          features: [feat],
          actions: [],
        });
      }
    },
    [clusterField, t, config, ref, saveClusterFeature, view, graphic, layer]
  );

  if (!filteredFeatures.length) return <div>loading</div>;

  return (
    <StyledCustomPopupWrapperContainer>
      <div
        style={{
          width: "100%",
          height: "100%",
          marginTop: 12,
        }}
      >
        <Swiper
          style={{
            height: "100%",
          }}
          onSwiper={(s) => {
            ref.current = s;
          }}
          allowTouchMove={false}
          preventInteractionOnTransition
          noSwiping
          initialSlide={initial}
          watchSlidesProgress
          resizeObserver={true}
        >
          <SwiperSlide
            style={{
              padding: "0px 8px",
              paddingBottom: 8,
            }}
            tabIndex={0}
          >
            {({ isActive }) =>
              isActive && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <StyledPopupTitleWrapper style={{ marginTop: 6 }}>
                    <StyledPopUpTitle style={{ justifyContent: "center" }}>
                      Total: {filteredFeatures.length}
                    </StyledPopUpTitle>
                  </StyledPopupTitleWrapper>
                  {filteredFeatures.map((feat, index) => {
                    const value = feat.attributes[clusterField?.name];

                    return (
                      <StyledNavButton
                        key={feat.uid}
                        style={{ width: "100%" }}
                        onClick={() => handleNavButtonClick(feat, index + 1)}
                      >
                        <span>
                          {value
                            ? renderCell(clusterField, value, t, config)
                            : featureIcons[feat.getObjectId()] || <span></span>}
                        </span>
                        <div
                          style={{
                            width: 20,
                            height: 20,
                            minWidth: 20,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <LeftArrowIcon
                            width="14"
                            height="14"
                            strokeWidth={1}
                          />
                        </div>
                      </StyledNavButton>
                    );
                  })}
                </div>
              )
            }
          </SwiperSlide>
          {cluster.clusterAttribute2 &&
            filteredFeatures.map((feat, index) => (
              <SwiperSlide
                key={feat.uid}
                tabIndex={index + 1}
                style={{
                  padding: "0px 8px",
                  paddingBottom: 8,
                }}
              >
                {({ isActive }) =>
                  isActive && (
                    <ClusterList
                      t={t}
                      clusterFeatures={features}
                      config={config}
                      layer={layer}
                      feature={feat}
                      goBack={() => {
                        ref.current?.slideTo(0);
                      }}
                      savePopup={() => saveClusterFeature(feat, index + 1)}
                      featureIcons={featureIcons}
                    />
                  )
                }
              </SwiperSlide>
            ))}
        </Swiper>
      </div>
    </StyledCustomPopupWrapperContainer>
  );
};

export default Cluster;
